import type { IconName } from '@vht/icons';
import { mayAccess, type Access, type AccessSession } from '@/lib/access';

export interface NavModule {
  to: string;
  labelKey: string;
  icon: IconName;
  /** What it takes to open it: one of these rights, or beheer. Mirrors the API — see `@/lib/access`. */
  access: Access;
  /**
   * Other paths that belong to this module, for the active state: a screen with a
   * prefix of its own (so `mayOpenPath` needs no exceptions) that still sits under
   * this tab. Plattegrond instellen lives under Plattegronden.
   */
  also?: readonly string[];
}
export interface NavWorld {
  key: string;
  labelKey: string;
  modules: NavModule[];
}

// Navigation at the top: the world bar, the module bar, the world switcher on a
// phone and the role filter all read from here (design_handoff_navigatie/).
//
// Only built screens appear here (conform AvdS); the coming modules are teased on
// the dashboard, never as dead "coming soon" items. When they arrive they go
// where the handoff README puts them: Planning (planbord, ritten), Materieel
// (voertuigen, eigenaren, standplaatsen move there from Brandstof), and offertes
// and documenten under Coördinatie.
//
// The dashboard is no world: it is the logo.
export const HOME = '/dashboard';

export const WORLDS: NavWorld[] = [
  {
    key: 'coordination',
    labelKey: 'nav.coordination',
    modules: [
      {
        to: '/orders',
        labelKey: 'nav.orders',
        icon: 'clipboard-list',
        access: ['coordination'],
      },
      {
        to: '/intake',
        labelKey: 'nav.intake',
        icon: 'download',
        access: ['coordination'],
      },
      // A report belongs to an order and goes on to Alfen — chain coordination.
      // The portal is the channel it arrives through, not the work.
      {
        to: '/groundwork',
        labelKey: 'nav.groundwork',
        icon: 'camera',
        access: ['groundwork'],
      },
      // A letter after a schouw: which extra work Alfen and the netbeheerder can
      // expect. It needs no dossier, so it has a right of its own.
      {
        to: '/additional-work',
        labelKey: 'nav.additionalWork',
        icon: 'euro',
        access: ['additional_work_notice'],
      },
    ],
  },
  {
    // Brandstofbeheer. `nav.world.fuel` is the short label for the world bar;
    // `nav.fuel` (Brandstofbeheer) stays for page headings and the dashboard.
    key: 'fuel',
    labelKey: 'nav.world.fuel',
    modules: [
      { to: '/fuel/entries', labelKey: 'nav.fuelEntries', icon: 'fuel', access: ['fuel'] },
      {
        to: '/fuel/vehicles',
        labelKey: 'nav.fuelVehicles',
        icon: 'truck',
        access: ['fuel'],
      },
      {
        to: '/fuel/owners',
        labelKey: 'nav.fuelOwners',
        icon: 'building',
        access: ['fuel'],
      },
      {
        to: '/fuel/homebases',
        labelKey: 'nav.fuelHomebases',
        icon: 'map-pin',
        access: ['fuel'],
      },
      {
        to: '/fuel/suppliers',
        labelKey: 'nav.fuelSuppliers',
        icon: 'package',
        access: ['fuel'],
      },
      {
        to: '/fuel/emission-factors',
        labelKey: 'nav.fuelEmissionFactors',
        icon: 'leaf',
        access: ['fuel'],
      },
    ],
  },
  {
    // Klaarzetlijst logistiek — mobile-first, used on the yard.
    key: 'logistics',
    labelKey: 'nav.logistics',
    modules: [
      {
        to: '/logistics/staging',
        labelKey: 'nav.staging',
        icon: 'warehouse',
        access: ['staging'],
      },
      // The same thing as the staging list, managed rather than operated
      // (`loading_places_write`).
      {
        to: '/logistics/loading-places',
        labelKey: 'nav.loadingPlaces',
        icon: 'warehouse',
        access: ['loading_places_write'],
      },
    ],
  },
  {
    // Opslag — waar elk station op het terrein staat (docs/storage.md). Werkt op een
    // tablet op het terrein, net als de klaarzetlijst. Stationtypen is beheer, dus
    // logistiek ziet één module en geen modulebalk.
    key: 'storage',
    labelKey: 'nav.storage',
    modules: [
      {
        to: '/storage/floorplans',
        labelKey: 'nav.floorplans',
        icon: 'map',
        access: ['storage'],
        also: ['/storage/floorplan-settings'],
      },
      {
        to: '/storage/station-types',
        labelKey: 'nav.stationTypes',
        icon: 'shapes',
        access: ['storage_admin'],
      },
      {
        // Voor wie Van Hooft opslaat (docs/storage.md §21): Alfen, en sinds 2026-10-27 meer.
        to: '/storage/owners',
        labelKey: 'nav.storageOwners',
        icon: 'building',
        access: ['storage_admin'],
      },
      {
        // Voor het management (docs/storage.md §20); alleen beheer, tot iemand het recht krijgt.
        to: '/storage/dashboard',
        labelKey: 'nav.storageDashboard',
        icon: 'activity',
        access: ['storage_dashboard'],
      },
    ],
  },
  {
    // Administratie (Maarten, 2026-10-13): quotations, and the companies Van Hooft
    // works for, from TALIS or a prospect (docs/quotations.md §3). Masterdata more
    // than one domain will use — quotations now, orders and invoicing later — so not
    // under Coördinatie, and not under Beheer, which is about who may see what.
    key: 'administration',
    labelKey: 'nav.world.administration',
    modules: [
      // Offertes (Maarten, 2026-10-13: naast Relaties). Een concept slaat zichzelf op;
      // uitbrengen en de PDF volgen (docs/quotations-bouwlijst.md).
      {
        to: '/quotations',
        labelKey: 'nav.quotations',
        icon: 'file-text',
        access: ['quotations'],
      },
      {
        to: '/relations',
        labelKey: 'nav.relations',
        icon: 'building',
        access: ['quotations'],
      },
      // De lijsten waar een offerte uit kiest: kraantypes, artikelen, standaardteksten
      // (docs/quotations.md §2). Een eigen recht, want ze raken ieders offerte.
      {
        to: '/quotation-lists',
        labelKey: 'nav.quotationLists',
        icon: 'clipboard-list',
        access: ['quotations_settings'],
      },
    ],
  },
  {
    // What spans every domain: people and parties.
    key: 'beheer',
    labelKey: 'nav.beheer',
    modules: [
      // Inzien kan een recht zijn (de planner beheert de meldingen van collega's
      // en komt daar via de gebruikersdetailpagina); wijzigen is beheer (docs/auth.md).
      { to: '/users', labelKey: 'nav.users', icon: 'users', access: ['users'] },
      {
        to: '/data-owners',
        labelKey: 'nav.dataOwners',
        icon: 'building',
        access: ['data_owners'],
      },
      // Wat elke rol mag. Zelf geen recht: wie rechten uitdeelt, is beheer.
      { to: '/permissions', labelKey: 'nav.permissions', icon: 'shield', access: 'admin' },
    ],
  },
];

/**
 * The worlds with only the modules this session may open. Nobody is offered a
 * screen that answers 403, and a world whose every module is filtered away
 * disappears with them.
 */
export function visibleWorlds(session: AccessSession | null | undefined): NavWorld[] {
  return WORLDS.map((w) => ({
    ...w,
    modules: w.modules.filter((m) => mayAccess(session, m.access)),
  })).filter((w) => w.modules.length > 0);
}

/**
 * Where someone lands after signing in, and at `/`. One world: its first module.
 * More than one: the dashboard. A fixed start module per role is a proposal in
 * the handoff, still open (README §Landen).
 */
export function landingPath(worlds: NavWorld[]): string {
  if (worlds.length === 1) return worlds[0]!.modules[0]!.to;
  return HOME;
}

/** The active world and module for a path. Longest prefix wins, like `mayOpenPath`. */
export function activeFor(
  pathname: string,
  worlds: NavWorld[],
): { world: NavWorld; module: NavModule } | null {
  let hit: { world: NavWorld; module: NavModule; length: number } | null = null;
  for (const world of worlds) {
    for (const module of world.modules) {
      for (const prefix of [module.to, ...(module.also ?? [])]) {
        const matches = pathname === prefix || pathname.startsWith(`${prefix}/`);
        if (matches && (!hit || prefix.length > hit.length)) {
          hit = { world, module, length: prefix.length };
        }
      }
    }
  }
  return hit && { world: hit.world, module: hit.module };
}
